import { cn } from '@sohanemon/utils';
import * as m from 'motion/react-m';
import {
	type MotionValue,
	useMotionTemplate,
	useMotionValue,
	useMotionValueEvent,
	useReducedMotion,
	useSpring,
	useTransform,
} from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';
import { MotionProvider } from '../motion';

type SpringConfig = { stiffness?: number; damping?: number; mass?: number };

const DEFAULT_SPRING: SpringConfig = { stiffness: 120, damping: 30, mass: 0.4 };

export type PathTravelerProps = {
	/** SVG path `d` string, drawn in viewBox coordinates. */
	path: string;
	/** [width, height] of the coordinate space the path is drawn in. */
	viewBox: readonly [width: number, height: number];
	/** 0 → 1 motion value (usually scrollYProgress or a useTransform of it). */
	progress: MotionValue<number>;
	/** The thing that travels (Lottie, image, icon, ...). */
	children: React.ReactNode;
	/** Travel from the path's end to its start instead. */
	reverse?: boolean;
	/** Remap progress, e.g. [0.2, 0.8] to only travel during the middle 60%. */
	range?: readonly [number, number];
	/** Spring smoothing config, or `false` to follow progress 1:1. */
	spring?: SpringConfig | false;
	/** Rotate to face the direction of travel. Default: true. */
	autoRotate?: boolean;
	/** Degrees added to the heading. 90 = child's "up" is its front. */
	rotationOffset?: number;
	/** viewBox units sampled each side of the point to get the heading. */
	lookahead?: number;
	/** CSS transform-origin of the rotating wrapper. */
	origin?: string;
	/** Draw the path in red so you can tune it. */
	debug?: boolean;
	/** Classes for the overlay (it's `absolute inset-0`, so the parent must be `relative`). */
	className?: string;
	/** Classes for the traveling item's wrapper (size it here, e.g. `w-40 xl:w-60`). */
	itemClassName?: string;
	/** Let the user drag the item off the path (e.g. rocket covering text). */
	drag?: boolean;
};

export function PathTraveler({
	path,
	viewBox,
	progress,
	children,
	reverse = false,
	range = [0, 1],
	spring = DEFAULT_SPRING,
	autoRotate = true,
	rotationOffset = 90,
	lookahead = 12,
	origin = 'top',
	debug = false,
	className,
	itemClassName,
	drag = false,
}: PathTravelerProps) {
	const [vbW, vbH] = viewBox;
	const pathRef = useRef<SVGPathElement>(null);
	const svgRef = useRef<SVGSVGElement>(null);
	const size = useRef({ width: vbW, height: vbH });
	const reduceMotion = useReducedMotion();

	const px = useMotionValue(0);
	const py = useMotionValue(0);
	const rotate = useMotionValue(0);
	const opacity = useMotionValue(0);
	const left = useMotionTemplate`${px}%`;
	const top = useMotionTemplate`${py}%`;

	const mapped = useTransform(progress, [0, 1], [range[0], range[1]], {
		clamp: true,
	});
	const sprung = useSpring(mapped, spring === false ? DEFAULT_SPRING : spring);
	const value = spring === false || reduceMotion ? mapped : sprung;

	const update = useCallback(
		(p: number) => {
			const el = pathRef.current;
			if (!el) return;

			const len = el.getTotalLength();
			const sign = reverse ? -1 : 1;
			const dist = (reverse ? 1 - p : p) * len;
			const clamp = (d: number) => Math.min(Math.max(d, 0), len);
			const pt = el.getPointAtLength(clamp(dist));

			px.set((pt.x / vbW) * 100);
			py.set((pt.y / vbH) * 100);
			opacity.set(1);

			if (!autoRotate) return;

			// sample both sides so the heading stays valid at the endpoints
			const ahead = el.getPointAtLength(clamp(dist + sign * lookahead));
			const behind = el.getPointAtLength(clamp(dist - sign * lookahead));

			// compute the angle in screen space (the svg is stretched)
			const dx = (ahead.x - behind.x) * (size.current.width / vbW);
			const dy = (ahead.y - behind.y) * (size.current.height / vbH);
			let target = (Math.atan2(dy, dx) * 180) / Math.PI + rotationOffset;

			// unwrap so it never spins the long way around
			target += Math.round((rotate.get() - target) / 360) * 360;
			rotate.set(target);
		},
		[
			autoRotate,
			lookahead,
			opacity,
			px,
			py,
			reverse,
			rotate,
			rotationOffset,
			vbH,
			vbW,
		],
	);

	useMotionValueEvent(value, 'change', update);

	// measure once + on resize instead of every frame
	useEffect(() => {
		const svg = svgRef.current;
		if (!svg) return;
		const ro = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect;
			size.current = { width, height };
			update(value.get());
		});
		ro.observe(svg);
		return () => ro.disconnect();
	}, [path, update, value]);

	// re-place the item when the geometry props change
	useEffect(() => {
		update(value.get());
	}, [update, value]);

	return (
		<MotionProvider feature={drag ? 'domMax' : 'domAnimation'}>
			<div className={cn('pointer-events-none absolute inset-0', className)}>
				<svg
					ref={svgRef}
					role="presentation"
					aria-hidden="true"
					viewBox={`0 0 ${vbW} ${vbH}`}
					preserveAspectRatio="none"
					className="size-full"
				>
					<path
						ref={pathRef}
						d={path}
						fill="none"
						stroke={debug ? 'red' : 'none'}
						strokeWidth={debug ? 2 : undefined}
						vectorEffect="non-scaling-stroke"
					/>
				</svg>

				<m.div className="absolute z-10 size-0" style={{ left, top, opacity }}>
					<m.div
						className={cn(
							'absolute',
							drag && 'pointer-events-auto cursor-grab active:cursor-grabbing',
							itemClassName,
						)}
						style={{ rotate, transformOrigin: origin }}
						drag={drag}
						dragMomentum={false}
					>
						{children}
					</m.div>
				</m.div>
			</div>
		</MotionProvider>
	);
}
